import { OFREPWebProvider } from '@openfeature/ofrep-web-provider';
import { type Client, type EventDetails, OpenFeature, type Provider, ProviderEvents } from '@openfeature/web-sdk';
import { type App, inject, type InjectionKey, onScopeDispose, readonly, type Ref, shallowRef } from 'vue';
import type { FlagKey } from './flags.gen.ts';

const FLAGS_CLIENT: InjectionKey<Client> = Symbol('flags client');
const OFF_UNTIL_FLIPT_ANSWERS = false;

export function flagsPlugin(provider: Provider) {
  return {
    install(app: App) {
      void OpenFeature.setProvider(provider);
      app.provide(FLAGS_CLIENT, OpenFeature.getClient());
    },
  };
}

export function stackFlagsPlugin() {
  return flagsPlugin(new OFREPWebProvider({ baseUrl: window.location.origin }));
}

export function useFlag(key: FlagKey): Readonly<Ref<boolean>> {
  const client = inject(FLAGS_CLIENT);
  if (!client) throw new Error(`useFlag('${key}') needs the flags plugin installed on the app`);

  const read = () => client.getBooleanValue(key, OFF_UNTIL_FLIPT_ANSWERS);
  const value = shallowRef(read());
  const refresh = () => (value.value = read());
  const refreshWhenChanged = (details?: EventDetails<ProviderEvents.ConfigurationChanged>) => {
    if (!details?.flagsChanged || details.flagsChanged.includes(key)) refresh();
  };

  client.addHandler(ProviderEvents.Ready, refresh);
  client.addHandler(ProviderEvents.ConfigurationChanged, refreshWhenChanged);
  onScopeDispose(() => {
    client.removeHandler(ProviderEvents.Ready, refresh);
    client.removeHandler(ProviderEvents.ConfigurationChanged, refreshWhenChanged);
  });

  return readonly(value);
}
